import { ARENA_WIDTH, ARENA_HEIGHT, BACKGROUND_COLOR } from '@mine-or-die/shared';

/**
 * Canvas window management with responsive scaling and bloom post-processing
 */
export class GameWindow {
  public readonly canvas: HTMLCanvasElement;
  public readonly ctx: CanvasRenderingContext2D;

  // Bloom effect canvases
  private bloomCanvas: HTMLCanvasElement;
  private bloomCtx: CanvasRenderingContext2D;
  private bloomEnabled: boolean = true;
  private bloomIntensity: number = 0.5;
  private bloomBlur: number = 20;

  private _scale: number = 1;
  private _offsetX: number = 0;
  private _offsetY: number = 0;

  constructor(canvasId: string = 'game') {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) {
      throw new Error(`Canvas element with id "${canvasId}" not found`);
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D rendering context');
    }

    this.canvas = canvas;
    this.ctx = ctx;

    // Create bloom canvas (offscreen)
    this.bloomCanvas = document.createElement('canvas');
    this.bloomCtx = this.bloomCanvas.getContext('2d')!;

    // Set up resize handling
    this.handleResize();
    window.addEventListener('resize', () => this.handleResize());

    // Disable context menu on canvas
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  /** Current scale factor for world-to-screen conversion */
  get scale(): number {
    return this._scale;
  }

  /** X offset for centering */
  get offsetX(): number {
    return this._offsetX;
  }

  /** Y offset for centering */
  get offsetY(): number {
    return this._offsetY;
  }

  /** Screen width */
  get width(): number {
    return this.canvas.width;
  }

  /** Screen height */
  get height(): number {
    return this.canvas.height;
  }

  /**
   * Manually trigger resize (useful when showing/hiding the canvas)
   */
  resize(): void {
    this.handleResize();
  }

  /**
   * Handle window resize - scales canvas to fit while maintaining aspect ratio
   */
  private handleResize(): void {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    // Set canvas to full window size
    this.canvas.width = windowWidth;
    this.canvas.height = windowHeight;

    // Update bloom canvas size
    this.bloomCanvas.width = windowWidth;
    this.bloomCanvas.height = windowHeight;

    // Calculate scale to fit arena in window
    const scaleX = windowWidth / ARENA_WIDTH;
    const scaleY = windowHeight / ARENA_HEIGHT;
    this._scale = Math.min(scaleX, scaleY);

    // Calculate offset to center the arena
    this._offsetX = (windowWidth - ARENA_WIDTH * this._scale) / 2;
    this._offsetY = (windowHeight - ARENA_HEIGHT * this._scale) / 2;
  }

  /**
   * Clear the canvas with background color
   */
  clear(): void {
    this.ctx.fillStyle = BACKGROUND_COLOR;
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Apply bloom post-processing effect
   * Call this after all rendering is complete
   */
  applyBloom(): void {
    if (!this.bloomEnabled) return;

    const ctx = this.ctx;
    const bloomCtx = this.bloomCtx;

    // Copy the main canvas to bloom canvas
    bloomCtx.clearRect(0, 0, this.bloomCanvas.width, this.bloomCanvas.height);
    bloomCtx.drawImage(this.canvas, 0, 0);

    // Apply blur filter to bloom canvas
    bloomCtx.filter = `blur(${this.bloomBlur}px)`;
    bloomCtx.globalCompositeOperation = 'source-over';
    bloomCtx.drawImage(this.bloomCanvas, 0, 0);
    bloomCtx.filter = 'none';

    // Composite bloom back onto main canvas with additive blending
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = this.bloomIntensity;
    ctx.drawImage(this.bloomCanvas, 0, 0);
    ctx.restore();
  }

  /**
   * Set bloom enabled/disabled
   */
  setBloomEnabled(enabled: boolean): void {
    this.bloomEnabled = enabled;
  }

  /**
   * Set bloom intensity (0-1)
   */
  setBloomIntensity(intensity: number): void {
    this.bloomIntensity = Math.max(0, Math.min(1, intensity));
  }

  /**
   * Set bloom blur amount
   */
  setBloomBlur(blur: number): void {
    this.bloomBlur = Math.max(1, blur);
  }

  /**
   * Convert world coordinates to screen coordinates
   */
  worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
    return {
      x: worldX * this._scale + this._offsetX,
      y: worldY * this._scale + this._offsetY,
    };
  }

  /**
   * Convert screen coordinates to world coordinates
   */
  screenToWorld(screenX: number, screenY: number): { x: number; y: number } {
    return {
      x: (screenX - this._offsetX) / this._scale,
      y: (screenY - this._offsetY) / this._scale,
    };
  }

  /**
   * Scale a world-space distance to screen-space
   */
  scaleDistance(worldDistance: number): number {
    return worldDistance * this._scale;
  }

  /**
   * Begin drawing with world-space transform applied
   */
  beginWorldTransform(): void {
    this.ctx.save();
    this.ctx.translate(this._offsetX, this._offsetY);
    this.ctx.scale(this._scale, this._scale);
  }

  /**
   * End world-space drawing
   */
  endWorldTransform(): void {
    this.ctx.restore();
  }

  /**
   * Get mouse position in world coordinates
   */
  getMouseWorldPos(event: MouseEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const screenX = event.clientX - rect.left;
    const screenY = event.clientY - rect.top;
    return this.screenToWorld(screenX, screenY);
  }
}
